<template>
  <div class="scoreboard">
    <div class="scoreboard-side scoreboard-side--left">
      <div class="name-slot">
        <input
          class="name-input"
          :class="{ 'name-input--active': isBlackActive }"
          type="text"
          maxlength="24"
          autocomplete="nickname"
          :value="blackName"
          :placeholder="phBlack"
          :aria-label="t('player.labelBlack')"
          @input="onBlack($event)"
        >
        <span v-if="showBlackCrown" class="winner-crown" aria-label="winner">👑</span>
      </div>
    </div>

    <div class="scoreboard-mid">
      <div class="score-item">
        <div class="piece-icon black" aria-hidden="true" />
        <div class="score-item-text">
          <div class="score-num">{{ scores.black }}</div>
        </div>
      </div>
      <div class="score-divider">{{ t('scoreboard.vs') }}</div>
      <div class="score-item score-item--right">
        <div class="score-item-text score-item-text--right">
          <div class="score-num">{{ scores.white }}</div>
        </div>
        <div class="piece-icon white" aria-hidden="true" />
      </div>
    </div>

    <div class="scoreboard-side scoreboard-side--right">
      <div class="name-slot">
        <input
          class="name-input"
          :class="{ 'name-input--active': isWhiteActive }"
          type="text"
          maxlength="24"
          autocomplete="nickname"
          :value="effectiveWhiteName"
          :placeholder="phWhite"
          :aria-label="t('player.labelWhite')"
          :disabled="isWhiteLocked"
          @input="onWhite($event)"
        >
        <span v-if="showWhiteCrown" class="winner-crown" aria-label="winner">👑</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { locale, t } from '@/i18n/index.js'

const props = defineProps({
  scores: {
    type: Object,
    required: true
  },
  blackName: {
    type: String,
    default: ''
  },
  whiteName: {
    type: String,
    default: ''
  },
  gameMode: {
    type: String,
    default: 'pvp'
  },
  currentPlayer: {
    type: Number,
    default: 1
  },
  gameOver: {
    type: Boolean,
    default: false
  },
  winner: {
    type: String,
    default: null
  }
})

const emit = defineEmits(['update:blackName', 'update:whiteName'])

const phBlack = computed(() => {
  void locale.value
  return t('player.default1')
})

const phWhite = computed(() => {
  void locale.value
  return props.gameMode === 'pvc' ? t('roles.ai') : t('player.default2')
})
const isWhiteLocked = computed(() => props.gameMode === 'pvc')
const effectiveWhiteName = computed(() =>
  isWhiteLocked.value ? t('roles.ai') : props.whiteName
)

const isBlackActive = computed(() => !props.gameOver && props.currentPlayer === 1)
const isWhiteActive = computed(() => !props.gameOver && props.currentPlayer === 2)
const showBlackCrown = computed(() => props.gameOver && props.winner === 'black')
const showWhiteCrown = computed(() => props.gameOver && props.winner === 'white')

function clip(s) {
  return String(s).slice(0, 24)
}

function onBlack(ev) {
  emit('update:blackName', clip(ev.target.value))
}

function onWhite(ev) {
  if (isWhiteLocked.value) return
  emit('update:whiteName', clip(ev.target.value))
}
</script>

<style scoped>
.scoreboard {
  box-sizing: border-box;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  align-items: center;
  gap: 10px 14px;
  background: #1f2937;
  border-radius: 12px;
  padding: 12px 14px;
  width: 100%;
  max-width: 100%;
}

.scoreboard-side {
  min-width: 0;
  display: flex;
  align-items: center;
}

.scoreboard-side--left {
  justify-content: flex-start;
}

.scoreboard-side--right {
  justify-content: flex-end;
}

.name-slot {
  position: relative;
  width: 100%;
  max-width: 160px;
}

.name-input {
  box-sizing: border-box;
  width: 100%;
  max-width: none;
  min-width: 0;
  padding: 6px 8px;
  font: inherit;
  font-size: 0.82rem;
  border-radius: 8px;
  border: 1px solid rgba(148, 163, 184, 0.35);
  background: rgba(15, 23, 42, 0.55);
  color: #f1f5f9;
}

.name-input::placeholder {
  color: #64748b;
  font-size: 0.78rem;
}

.winner-crown {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 0.9rem;
  line-height: 1;
  pointer-events: none;
}

.name-input:focus {
  outline: none;
  border-color: rgba(34, 197, 94, 0.55);
  box-shadow: 0 0 0 1px rgba(34, 197, 94, 0.2);
}

.name-input--active {
  color: #b91c1c;
  font-weight: 700;
  border-color: rgba(185, 28, 28, 0.62);
  box-shadow: 0 0 0 1px rgba(185, 28, 28, 0.22);
}

.scoreboard-mid {
  display: flex;
  align-items: center;
  gap: 20px;
  flex-shrink: 0;
}

.score-item {
  display: flex;
  align-items: center;
  gap: 10px;
}

.score-item--right {
  flex-direction: row;
}

.score-item-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 32px;
}

.score-item-text--right {
  align-items: flex-end;
  text-align: right;
}

.piece-icon {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  flex-shrink: 0;
}

.piece-icon.black {
  background: #0f172a;
  border: 2px solid #334155;
}

.piece-icon.white {
  background: #f8fafc;
  border: 2px solid #cbd5e1;
}

.score-num {
  font-size: 1.6rem;
  font-weight: 700;
  line-height: 1.1;
  text-align: center;
}

.score-divider {
  color: #94a3b8;
  font-size: 1.2rem;
  flex-shrink: 0;
}

@media (max-width: 560px) {
  .scoreboard {
    grid-template-columns: 1fr 1fr;
    grid-template-rows: auto auto;
  }

  .scoreboard-mid {
    grid-column: 1 / -1;
    grid-row: 1;
    justify-content: center;
  }

  .scoreboard-side--left {
    grid-column: 1;
    grid-row: 2;
  }

  .scoreboard-side--right {
    grid-column: 2;
    grid-row: 2;
    justify-content: flex-end;
  }

  .name-input {
    max-width: none;
  }

  .name-slot {
    max-width: none;
  }
}
</style>
